import { isBefore, isAfter } from 'date-fns';
import { Item, PromoCode } from '@root/src/types';

export const promoCodeIsActive = (promoCode: PromoCode, now = new Date()) => {
  if (!promoCode.active) {
    return false;
  }

  const { startDate, endDate } = promoCode;

  const isBeforeStartDate = isBefore(now, startDate ?? now);
  const isAfterExpirationDate = isAfter(now, endDate ?? now);

  return !(isBeforeStartDate || isAfterExpirationDate);
};

export const itemContractMatchesPromoCode = (item: Item, promoCode: PromoCode) => {
  if (!promoCode.discountRestrictionContract) {
    return true;
  }

  const contract = promoCode.discountRestrictionContract.toLowerCase();

  const isRetailBundle = item.is_retail ?? item.isRetail ?? false;
  const isPurchaseItem = item.type?.toLowerCase() === 'purchase';

  if (contract === 'purchase') {
    return isRetailBundle || isPurchaseItem;
  }

  return !isRetailBundle && !isPurchaseItem;
};

export const itemBelongsToCategoryInPromoCode = (item: Item, promoCode: PromoCode) => {
  if (promoCode.discountRestrictionCategories.length === 0) {
    return true;
  }

  const includesJacketOrPants = promoCode.discountRestrictionCategories.some((category) => {
    return category.toLowerCase() === 'jacket' || category.toLowerCase() === 'pants';
  });

  if (item.category === 'preconfigured' && includesJacketOrPants) {
    return true;
  }

  return promoCode.discountRestrictionCategories.some(
    (category) => item.category?.toLowerCase() === category.toLowerCase()
  );
};

export const promoCodeAppliesToItem = (item: Item, promoCode: PromoCode) => {
  return (
    promoCodeIsActive(promoCode) &&
    itemContractMatchesPromoCode(item, promoCode) &&
    itemBelongsToCategoryInPromoCode(item, promoCode)
  );
};

export const getPromotionalPrice = (item: Item, promoCode: PromoCode) => {
  let actualPrice = Number(item.cost ?? 0);
  let discountAmount = Number.parseInt(promoCode.discountAmount);

  if (promoCode.discountType === 'percent') {
    discountAmount = actualPrice * (discountAmount / 100);
  } else if (actualPrice < discountAmount) {
    discountAmount = actualPrice;
  }

  const discountedPrice = actualPrice - discountAmount;

  if (!Number.isInteger(discountedPrice)) {
    // Rounds to the nearest dollar amount
    return discountedPrice.toFixed(0);
  }

  return String(discountedPrice);
};
